import {
  Module,
  VuexModule,
  Mutation,
  Action,
  config
} from "vuex-module-decorators";
import { PropertiesData } from "@/types/tree";
import VueSimpleDeepset from "@/util/vue-simple-deepset";
import { CSSSelector } from "@/types/selectors";
import { throttlePropertyChange } from "@/util/throttle-property-change";
import {
  PresetStorePropertyChange,
  SelectorStorePropertyChange,
  SettingStorePropertyChange
} from "@/types/store-property-change";
import { GlobalColor, GlobalColorsV } from "@/types/colors";
import lodash, { isEqual } from "lodash";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import defensiveCopy from "@/util/defensive-copy";
import { breakdanceUndo } from "@/lib/undo/undo";
import { TypographyPreset } from "@/types/typography";
import { generateTransactionKeyFromGenericStorePropertyChange } from "@/lib/undo/transaction-key.utils";
import {
  GlobalSettingsRevision,
  PresetsRevision,
  SelectorsRevision
} from "@/types/revisions";
import { compare } from "rfc6902-json-diff";
import { applyPatchWithVueReactivity } from "@/util/fast-json-patch-reactive-ops";
import { ElementPreset } from "@/types/presets";
import { AISettings } from "@/lib/ai/types";

/**
 * IMPORTANT:
 * READ & UNDERSTAND THE FOLLOWING BEFORE ADDING
 * PROPERTIES TO THE DOCUMENT OR GLOBAL STORE
 *
 * <see the comment in the document store>
 */

export const GLOBAL_SETTINGS_PATHS = {
  globalColors: "settings.colors.palette.colors",
  globalGradients: "settings.colors.palette.gradients",
  typographyPresets: "settings.typography.global_typography.typography_presets"
};

config.rawError = true;
@Module({ name: "global", namespaced: true })
export default class GlobalModule extends VuexModule {
  // Global settings
  globalSettings: PropertiesData = {};
  untouchedGlobalSettings: PropertiesData = {};

  // CSS selectors
  cssSelectors: CSSSelector[] = [];
  untouchedCssSelectors: CSSSelector[] = [];

  // Presets
  presets: ElementPreset[] = [];
  untouchedPresets: ElementPreset[] = [];

  aiSettings: AISettings = {
    isApiKeySet: false,
    isStreamingEnabled: false,
    model: "",
    prompt: {
      businessName: "",
      businessDescription: "",
      qualifiers: "",
      presets: []
    }
  };
  untouchedAiSettings: AISettings | null = null;

  get globalColors(): GlobalColor[] {
    const maybeGlobalColors = lodash.get(
      this.globalSettings,
      GLOBAL_SETTINGS_PATHS.globalColors
    );

    return pipe(
      maybeGlobalColors,
      GlobalColorsV.decode,
      fold(
        () => [],
        globalColors => globalColors
      )
    );
  }

  get globalGradients(): GlobalColor[] {
    const maybeGlobalGradients = lodash.get(
      this.globalSettings,
      GLOBAL_SETTINGS_PATHS.globalGradients
    );

    return pipe(
      maybeGlobalGradients,
      GlobalColorsV.decode,
      fold(
        () => [],
        globalColors => globalColors
      )
    );
  }

  get settingsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedGlobalSettings),
      defensiveCopy(this.globalSettings)
    );
  }

  get selectorsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedCssSelectors),
      defensiveCopy(this.cssSelectors)
    );
  }

  get presetsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedPresets),
      defensiveCopy(this.presets)
    );
  }

  get aiSettingsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedAiSettings),
      defensiveCopy(this.aiSettings)
    );
  }

  @Action
  setGlobalColors(colors: GlobalColor[]) {
    this.throttledGlobalSettingsPropertyChange({
      path: GLOBAL_SETTINGS_PATHS.globalColors,
      value: colors,
      meta: {
        snapshotLabel: "Change Global Colors"
      }
    });
  }

  @Action
  addGlobalColor(color: GlobalColor) {
    this.throttledGlobalSettingsPropertyChange({
      path: GLOBAL_SETTINGS_PATHS.globalColors,
      value: [...defensiveCopy(this.globalColors), color],
      meta: {
        snapshotLabel: "Add Global Color"
      }
    });
  }

  @Action
  addGlobalGradient(color: GlobalColor) {
    this.throttledGlobalSettingsPropertyChange({
      path: GLOBAL_SETTINGS_PATHS.globalGradients,
      value: [...defensiveCopy(this.globalGradients), color],
      meta: {
        snapshotLabel: "Add Global Gradient"
      }
    });
  }

  // Selectors

  @Action
  throttledSelectorPropertyChange(change: SelectorStorePropertyChange) {
    throttlePropertyChange(
      `${change.selector.name}${change.path}`,
      this.unthrottledSelectorPropertyChangeAction,
      change,
      "selectorSettings"
    );
  }

  @Action
  unthrottledSelectorPropertyChangeAction(change: SelectorStorePropertyChange) {
    breakdanceUndo.transaction(
      () => {
        this.unthrottledSelectorPropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "SelectorProperty"
      )
    );
  }

  @Mutation
  unthrottledSelectorPropertyChange({
    selector,
    path,
    value
  }: SelectorStorePropertyChange) {
    /* TODO: handle this like document.propertyChangedMutation.
    don't pass the reference into the mutation handler.
    instead, pass in the ID of the selector, then look it up here. */
    VueSimpleDeepset(selector, path, value);
  }

  // Presets
  @Action
  throttledPresetPropertyChange(change: PresetStorePropertyChange) {
    throttlePropertyChange(
      `${change.preset.id}${change.path}`,
      this.unthrottledPresetPropertyChangeAction,
      change,
      "presetSettings"
    );
  }

  @Action
  unthrottledPresetPropertyChangeAction(change: PresetStorePropertyChange) {
    breakdanceUndo.transaction(
      () => {
        this.unthrottledPresetPropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "PresetProperty"
      )
    );
  }

  @Mutation
  unthrottledPresetPropertyChange({
    preset,
    path,
    value
  }: PresetStorePropertyChange) {
    /* TODO: handle this like document.propertyChangedMutation.
    don't pass the reference into the mutation handler.
    instead, pass in the ID of the preset, then look it up here. */
    VueSimpleDeepset(preset, path, value);
  }

  // Global settings

  @Action
  throttledGlobalSettingsPropertyChange({
    path,
    value,
    meta
  }: SettingStorePropertyChange) {
    throttlePropertyChange(
      path,
      this.unthrottledGlobalSettingsPropertyChangeAction,
      {
        path,
        value,
        meta
      },
      "globalSettings"
    );
  }

  @Action
  protected unthrottledGlobalSettingsPropertyChangeAction(
    change: SettingStorePropertyChange
  ): void {
    // TODO move breakdanceUndo.transaction-related logic somehow to BreakdanceUndo?
    breakdanceUndo.transaction(
      () => {
        this.unthrottledGlobalSettingsPropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "GlobalSettings"
      )
    );
  }

  @Mutation
  unthrottledGlobalSettingsPropertyChange({
    path,
    value
  }: SettingStorePropertyChange) {
    VueSimpleDeepset(this.globalSettings, path, value);
  }

  @Mutation
  setGlobalSettings(globalSettings: PropertiesData) {
    // TODO find where PHP sends globalSettings as [], then use stdObject, then ditch this logic
    // TODO VueSimpleDeepset isn't needed here
    // blank global settings should be a {}... but the way PHP works when JSON encoding, that's tricky
    // so we enforce it to be a {} here (since we can't use VueSimpleDeepset on an array / falsey value)
    // todo: maybe better to make VueSimpleDeepset convert undefined and shit to object?
    // or maybe an implicit conversion would just allow bugs to slip through that are really bugs elsewhere?
    if (globalSettings) {
      if (
        !globalSettings ||
        (Array.isArray(globalSettings) && globalSettings.length === 0)
      ) {
        VueSimpleDeepset(this, "globalSettings", {});
      } else {
        const globalSettingsDiff = compare(this.globalSettings, globalSettings);
        applyPatchWithVueReactivity(this.globalSettings, globalSettingsDiff);
      }
    }
  }

  @Mutation
  setAiSettings(aiSettings: AISettings) {
    this.aiSettings = aiSettings;
  }

  @Mutation
  restoreGlobalSettingsRevision(revision: GlobalSettingsRevision) {
    this.globalSettings = defensiveCopy(revision.globalSettings);
  }

  @Mutation
  restoreSelectorsRevision(revision: SelectorsRevision) {
    this.cssSelectors = defensiveCopy(revision.selectors);
  }

  @Mutation
  restorePresetsRevision(revision: PresetsRevision) {
    this.presets = defensiveCopy(revision.presets);
  }

  @Mutation
  setSelectors(selectors: CSSSelector[]) {
    this.cssSelectors = selectors;
  }

  @Mutation
  setPresets(presets: ElementPreset[]) {
    this.presets = presets;
  }

  get typographyPresets(): TypographyPreset[] | undefined {
    return lodash.get(
      this.globalSettings,
      GLOBAL_SETTINGS_PATHS.typographyPresets,
      []
    );
  }

  @Mutation
  markCurrentGlobalSettingsAsUntouched() {
    this.untouchedGlobalSettings = defensiveCopy(this.globalSettings);
  }

  @Mutation
  markCurrentSelectorsAsUntouched() {
    this.untouchedCssSelectors = defensiveCopy(this.cssSelectors);
  }

  @Mutation
  markCurrentPresetsAsUntouched() {
    this.untouchedPresets = defensiveCopy(this.presets);
  }

  @Mutation
  markCurrentAiSettingsAsUntouched() {
    this.untouchedAiSettings = defensiveCopy(this.aiSettings);
  }
}
